import { ChangeEvent, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'state/store';
import { useWorkflowStateMachine } from 'hooks/useWorkflowStateMachine';
import { hasExternalPricingRulesCustomerId, getAuthenticationType } from 'state/brand/brand.slice';
import { getNeedManualTableId, getTableId, updateAppState } from 'state/app';
import { getAccessToken, getCustomerName, updateCustomer } from 'state/customer/customer.slice';
import { useSearchParams } from 'react-router-dom';

export const useAdditionalInformationsVM = () => {
    const CUSTOMER_NAME_LENGTH_MIN = 2;
    const dispatch = useDispatch();
    const customerName = useAppSelector(getCustomerName);
    const { goNext } = useWorkflowStateMachine();
    const hasExternalCustomer = useAppSelector(hasExternalPricingRulesCustomerId);
    const authenticationType = useAppSelector(getAuthenticationType);
    const tableNumber = useAppSelector(getTableId);
    const needManualTableId = useAppSelector(getNeedManualTableId);
    const isAuthenticated = useAppSelector(getAccessToken);
    const [searchParams, setSearchParams] = useSearchParams();

    const shouldDisplayExternalCustomer =
        hasExternalCustomer && authenticationType === 'Hybrid' && !isAuthenticated;

    const { t } = useTranslation();

    const handleUpdateCustomerName = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            dispatch(
                updateCustomer({
                    customerName: event.target.value,
                }),
            );
        },
        [dispatch],
    );

    const handleUpdateTableNumber = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const tableId = event.target.value !== '' ? event.target.value : null;
            if (tableId) {
                setSearchParams({ ...searchParams, tableId });
            } else {
                const { tableId: _, ...searchParamsWithoutTableId } = Object.fromEntries(
                    searchParams.entries(),
                );
                setSearchParams({ ...searchParamsWithoutTableId });
            }
            dispatch(
                updateAppState({
                    tableId,
                }),
            );
        },
        [dispatch, setSearchParams, searchParams],
    );

    return {
        buttonText: t('go_to_payment'),
        customerName,
        tableNumber,
        handleUpdateCustomerName,
        handleUpdateTableNumber,
        needManualTableId,
        goNext,
        CUSTOMER_NAME_LENGTH_MIN,
        shouldDisplayExternalCustomer,
        isAuthenticated,
    };
};
